import type { User, UserEntry, UserWithLawFirm } from '@common/types/users';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import LexoSwitch from '@/components/LexoSwitch';
import { updateUserNotifications } from '@/services/UserService';
import { theme } from '@/theme';
import { errorSnackbar } from '@/utils/snackbar';

interface Props {
    users: UserEntry[];
    usersLoading: boolean;
    selectedUserId: number | null;
    selectedUser: UserWithLawFirm | null;
    onSelectUser(id: number | null): void;
}

function getNotificationSettings(selectedUser: User | null) {
    return {
        isQuoteFormSentNotifEnabled: selectedUser?.isQuoteFormSentNotifEnabled ?? true,
        isQuoteApprovalNotifEnabled: selectedUser?.isQuoteApprovalNotifEnabled ?? true,
        isQuoteReminderNotifEnabled: selectedUser?.isQuoteReminderNotifEnabled ?? true,
        isQuoteDeclinedNotifEnabled: selectedUser?.isQuoteDeclinedNotifEnabled ?? true,
        isIntakeSentNotifEnabled: selectedUser?.isIntakeSentNotifEnabled ?? true,
        isDraftIntakeNotifEnabled: selectedUser?.isDraftIntakeNotifEnabled ?? true,
        isIntakeReminderNotifEnabled: selectedUser?.isIntakeReminderNotifEnabled ?? true,
        isPostIntakeNotifEnabled: selectedUser?.isPostIntakeNotifEnabled ?? true,
    };
}

export default function Notification({
    users,
    usersLoading,
    selectedUserId,
    selectedUser,
    onSelectUser,
}: Props) {
    const queryClient = useQueryClient();

    const settings = useMemo(() => getNotificationSettings(selectedUser ?? null), [selectedUser]);

    // Mutation with optimistic update
    const mutation = useMutation({
        mutationFn: async (nextSettings: typeof settings) =>
            updateUserNotifications(selectedUserId as number, nextSettings),
        onMutate: async (nextSettings) => {
            await queryClient.cancelQueries({ queryKey: ['user', selectedUserId] });

            const previous = queryClient.getQueryData<UserWithLawFirm>(['user', selectedUserId]);

            queryClient.setQueryData<UserWithLawFirm>(['user', selectedUserId], (old) =>
                old ? { ...old, ...nextSettings } : old,
            );

            return { previous };
        },
        onError: (_err, _next, context) => {
            if (context?.previous) {
                queryClient.setQueryData(['user', selectedUserId], context.previous);
            }
            errorSnackbar('Failed to update notification settings');
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['user', selectedUserId] });
        },
    });

    const handleToggleChange = (key: keyof typeof settings, value: boolean) => {
        const next = { ...settings, [key]: value };
        mutation.mutate(next);
    };

    const handleUserChange = (event: SelectChangeEvent<string>) => {
        const id = Number(event.target.value);
        const next = Number.isNaN(id) ? null : id;
        onSelectUser(next);
    };

    const toggleRows: {
        key: keyof typeof settings;
        label: string;
        description: string;
    }[] = [
        {
            key: 'isQuoteFormSentNotifEnabled',
            label: 'Quote form sent emails',
            description: "User is CC'd on initial quote form link emails sent to clients",
        },
        {
            key: 'isQuoteApprovalNotifEnabled',
            label: 'Quote approval emails',
            description: "User is CC'd on quote approval emails sent to clients",
        },
        {
            key: 'isQuoteReminderNotifEnabled',
            label: 'Quote reminder emails',
            description: "User is CC'd on quote reminder emails sent to clients",
        },
        {
            key: 'isQuoteDeclinedNotifEnabled',
            label: 'Quote declined emails',
            description: "User is CC'd when quote requests are declined and emails sent to clients",
        },
        {
            key: 'isIntakeSentNotifEnabled',
            label: 'Intake sent emails',
            description: "User is CC'd on initial intake form link emails sent to clients",
        },
        {
            key: 'isDraftIntakeNotifEnabled',
            label: 'Draft intake emails',
            description: "User is CC'd when a draft intake email is sent",
        },
        {
            key: 'isIntakeReminderNotifEnabled',
            label: 'Intake reminder emails',
            description: "User is CC'd on intake reminder emails sent to clients",
        },
        {
            key: 'isPostIntakeNotifEnabled',
            label: 'Intake update emails',
            description: "User is CC'd on intake update emails sent to clients",
        },
    ];

    return (
        <Stack spacing={2}>
            <Typography variant="h1">Email Notifications</Typography>
            <FormControl sx={{ width: '100%', maxWidth: '600px' }}>
                <InputLabel>Select User</InputLabel>
                <Select
                    label="Select User"
                    value={String(selectedUserId ?? '')}
                    onChange={handleUserChange}
                >
                    {usersLoading && <MenuItem disabled>Loading users…</MenuItem>}
                    {!usersLoading &&
                        users.map((u) => (
                            <MenuItem key={u.userId} value={u.userId}>
                                {u.fullName}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
            <Table size="small" sx={{ width: '100%', maxWidth: '600px' }}>
                <TableBody>
                    {toggleRows.map((row) => (
                        <TableRow key={row.key}>
                            <TableCell sx={{ paddingLeft: 0 }}>
                                <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {row.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.neutral[500], mt: 0.5 }}
                                >
                                    {row.description}
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ paddingRight: 0, verticalAlign: 'top' }}>
                                <LexoSwitch
                                    checked={Boolean(settings[row.key])}
                                    onChange={(e) => {
                                        if (mutation.isPending) return; // Ignore click while saving
                                        handleToggleChange(row.key, e.target.checked);
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Stack>
    );
}
